<template>
  <div class="filter-panel">
    <div class="filter-header" @click="toggleExpanded">
      <h3 style="margin: 0; font-size: 14px; font-weight: 600">
        {{ s__('TaskFilter|Filters') }}
        <span v-if="activeFiltersCount > 0" class="active-count">({{ activeFiltersCount }})</span>
      </h3>
      <span class="expand-icon">{{ isExpanded ? '▼' : '▶' }}</span>
    </div>

    <div v-if="isExpanded" ref="filterContentRef" class="filter-content" @click.stop>
      <div class="filter-row">
        <div class="filter-col-full">
          <label>{{ s__('TaskFilter|Search') }}</label>
          <input
            v-model="localFilters.search"
            type="text"
            :placeholder="
              s__('TaskFilter|Search in title, description, and labels (min 3 chars)...')
            "
            :class="{ 'search-input-invalid': searchError }"
            style="width: 100%; padding: 6px 10px; border: 1px solid #ced4da; border-radius: 4px"
            @keyup.enter="applyFilters"
          />
          <div
            v-if="searchError"
            class="search-error"
            style="color: #dc3545; font-size: 12px; margin-top: 4px"
          >
            {{ s__('TaskFilter|Search requires at least 3 characters') }}
          </div>
        </div>
      </div>

      <div
        v-if="
          showTaskTypeFilter &&
          (taskCategories.length > 1 ||
            (groupedTaskCategories && Object.keys(groupedTaskCategories).length > 0))
        "
        class="filter-row"
      >
        <div class="filter-col-full">
          <MultiSelect
            :key="`task-categories-${taskCategories.length}`"
            :options="taskCategoryOptions"
            :selected-ids="localFilters.taskCategoryIds || []"
            :label="s__('TaskFilter|Task categories (any)')"
            :placeholder="s__('TaskFilter|Select task categories...')"
            :search-placeholder="s__('TaskFilter|Search categories...')"
            :no-results-text="s__('TaskFilter|No categories found')"
            @update:selected-ids="localFilters.taskCategoryIds = $event"
          />
        </div>
      </div>

      <div v-if="!hideAuthors || !hideAssignees" class="filter-row">
        <div v-if="!hideAuthors" class="filter-col">
          <MultiSelect
            :key="`authors-${memberOptions.length}`"
            :options="memberOptions"
            :selected-ids="localFilters.authorIds || []"
            :label="s__('TaskFilter|Authors (any)')"
            :placeholder="s__('TaskFilter|Select authors...')"
            :search-placeholder="s__('TaskFilter|Search authors...')"
            :no-results-text="s__('TaskFilter|No authors found')"
            @update:selected-ids="localFilters.authorIds = $event"
          />
        </div>
        <div v-if="!hideAssignees" class="filter-col">
          <MultiSelect
            :key="`assignees-${memberOptions.length}`"
            :options="[
              { id: 'none', title: s__('TaskFilter|Unassigned'), color: '#9CA3AF' },
              ...memberOptions,
            ]"
            :selected-ids="localFilters.assigneeIds"
            :label="s__('TaskFilter|Assignees (any)')"
            :placeholder="s__('TaskFilter|Select assignees...')"
            :search-placeholder="s__('TaskFilter|Search assignees...')"
            :no-results-text="s__('TaskFilter|No assignees found')"
            @update:selected-ids="handleAssigneeIdsUpdate"
          />
        </div>
      </div>

      <div class="filter-row">
        <div class="filter-col">
          <MultiSelect
            :key="`priorities-${priorities.length}`"
            :options="[
              { id: 'none', title: s__('TaskFilter|None'), color: '#9CA3AF' },
              ...sortedPriorities.map(p => ({ id: p.value, title: p.label, color: '#6B7280' })),
            ]"
            :selected-ids="localFilters.priorities || []"
            :label="s__('TaskFilter|Priorities (any)')"
            :placeholder="s__('TaskFilter|Select priorities...')"
            :search-placeholder="s__('TaskFilter|Search priorities...')"
            :no-results-text="s__('TaskFilter|No priorities found')"
            @update:selected-ids="localFilters.priorities = $event"
          />
        </div>
        <div class="filter-col">
          <MultiSelect
            :key="`statuses-${(workflowStatuses || []).length}`"
            :options="workflowStatusOptions"
            :selected-ids="localFilters.workflowStatusIds || []"
            :label="s__('TaskFilter|Statuses (any)')"
            :placeholder="s__('TaskFilter|Select statuses...')"
            :search-placeholder="s__('TaskFilter|Search statuses...')"
            :no-results-text="s__('TaskFilter|No statuses found')"
            @update:selected-ids="localFilters.workflowStatusIds = $event"
          />
        </div>
      </div>

      <!-- Projects (only for personal boards) -->
      <div v-if="isPersonalProject && projects.length > 0" class="filter-row">
        <MultiSelect
          :key="`projects-${projects.length}`"
          :options="projects.map(p => ({ id: p.id, title: p.title, color: '#6B7280' }))"
          :selected-ids="localFilters.projectIds"
          :label="s__('TaskFilter|Projects (any)')"
          :placeholder="s__('TaskFilter|Select projects...')"
          :search-placeholder="s__('TaskFilter|Search projects...')"
          :no-results-text="s__('TaskFilter|No projects found')"
          @update:selected-ids="localFilters.projectIds = $event"
        />
      </div>

      <!-- Project Labels -->
      <div
        v-if="(labelOptions.length > 0 || labels.length > 0) && !hideProjectLabels"
        class="filter-row"
      >
        <MultiSelect
          :key="`labels-${labelOptions.length || labels.length}`"
          :options="labelOptions"
          :selected-ids="localFilters.labelIds"
          :label="
            personalLabelsEnabled
              ? s__('TaskFilter|Project Labels (any)')
              : s__('TaskFilter|Labels (any)')
          "
          :placeholder="s__('TaskFilter|Select labels...')"
          :search-placeholder="s__('TaskFilter|Search labels...')"
          :no-results-text="s__('TaskFilter|No labels found')"
          @update:selected-ids="localFilters.labelIds = $event"
        />
      </div>

      <!-- Personal Labels -->
      <div v-if="personalLabelsEnabled && personalLabels.length > 0" class="filter-row">
        <MultiSelect
          :key="`personal-labels-${personalLabels.length}`"
          :options="personalLabels"
          :selected-ids="localFilters.personalLabelIds"
          :label="s__('TaskFilter|Personal Labels (any)')"
          :placeholder="s__('TaskFilter|Select personal labels...')"
          :search-placeholder="s__('TaskFilter|Search personal labels...')"
          :no-results-text="s__('TaskFilter|No personal labels found')"
          @update:selected-ids="localFilters.personalLabelIds = $event"
        />
      </div>

      <div class="filter-row">
        <div class="filter-col">
          <label>{{ s__('TaskFilter|Due from') }}</label>
          <input v-model="localFilters.dueFrom" type="date" />
        </div>
        <div class="filter-col">
          <label>{{ s__('TaskFilter|Due to') }}</label>
          <input v-model="localFilters.dueTo" type="date" />
        </div>
      </div>

      <div class="filter-row">
        <div class="filter-col">
          <label>{{ s__('TaskFilter|Due in') }}</label>
          <input
            v-model.number="localFilters.dueInDays"
            type="number"
            min="1"
            max="99"
            :placeholder="s__('TaskFilter|Days (1-99)')"
            :class="{ 'input-invalid': dueInDaysError }"
            style="width: 100%; padding: 6px 10px; border: 1px solid #ced4da; border-radius: 4px"
          />
          <div
            v-if="dueInDaysError"
            class="input-error"
            style="color: #dc3545; font-size: 12px; margin-top: 4px"
          >
            {{ dueInDaysError }}
          </div>
        </div>
        <div class="filter-col">
          <label class="checkbox-label">
            <input v-model="localFilters.includeOverdue" type="checkbox" />
            {{ s__('TaskFilter|Include overdue') }}
          </label>
        </div>
      </div>

      <div class="filter-row">
        <div v-if="!hideIncludeFinished" class="filter-col">
          <label class="checkbox-label">
            <input v-model="localFilters.includeFinished" type="checkbox" />
            {{ s__('TaskFilter|Include finished') }}
          </label>
        </div>
        <div class="filter-col">
          <label class="checkbox-label">
            <input v-model="localFilters.includeArchived" type="checkbox" />
            {{ s__('TaskFilter|Include archived') }}
          </label>
        </div>
      </div>

      <div v-if="!hideApplyButton" class="filter-actions">
        <button class="btn-primary" @click="applyFilters">{{ __('Apply') }}</button>
        <button class="btn-secondary" @click="resetFilters">{{ s__('TaskFilter|Reset') }}</button>
      </div>
      <div v-else class="filter-actions">
        <button class="btn-secondary" @click="resetFilters">{{ s__('TaskFilter|Reset') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __ } from '@/locale'
import UserSelect from '../common/UserSelect.vue'
import LabelFilterCheckboxes from '../common/LabelFilterCheckboxes.vue'
import MultiSelect from '../common/MultiSelect.vue'

const props = defineProps({
  members: { type: Array, default: () => [] },
  priorities: { type: Array, default: () => [] },
  workflowStatuses: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  taskCategories: { type: Array, default: () => [] },
  initialFilters: { type: Object, default: () => ({}) },
  isPersonalProject: { type: Boolean, default: false },
  showTaskTypeFilter: { type: Boolean, default: false },
  projectId: { type: [String, Number], default: null },
  personalLabelsEnabled: { type: Boolean, default: false },
  groupedWorkflowStatuses: { type: Object, default: null },
  groupedTaskCategories: { type: Object, default: null },
  groupedMembers: { type: Object, default: null }, // { [projectTitle]: [members] }
  groupedLabels: { type: Object, default: null }, // { [projectTitle]: [labels] }
  hideIncludeFinished: { type: Boolean, default: false }, // Hide "Include finished" checkbox (for boards)
  projects: { type: Array, default: () => [] }, // Projects for filtering (only for personal boards)
  hideApplyButton: { type: Boolean, default: false }, // Hide Apply button and auto-emit changes
  hideAssignees: { type: Boolean, default: false }, // Hide Assignees filter
  hideAuthors: { type: Boolean, default: false }, // Hide Authors filter
  hideProjectLabels: { type: Boolean, default: false }, // Hide Project Labels filter
  initiallyExpanded: { type: Boolean, default: false },
  lockExpanded: { type: Boolean, default: false },
})

const emit = defineEmits(['apply'])

// Sort priorities so higher priorities appear first
const sortedPriorities = computed(() => {
  return [...props.priorities].sort((a, b) => a.value - b.value)
})

// Compute task category options for MultiSelect
const taskCategoryOptions = computed(() => {
  if (props.groupedTaskCategories && Object.keys(props.groupedTaskCategories).length > 0) {
    // Flatten grouped categories
    const options = []
    for (const [projectTitle, categories] of Object.entries(props.groupedTaskCategories)) {
      for (const category of categories) {
        options.push({
          id: category.id.toString(),
          title: `${projectTitle} - ${category.name}`,
          color: '#6B7280',
        })
      }
    }
    return options
  } else {
    return props.taskCategories.map(c => ({
      id: c.id.toString(),
      title: c.name,
      color: '#6B7280',
    }))
  }
})

// Compute workflow status options for MultiSelect
const workflowStatusOptions = computed(() => {
  if (props.groupedWorkflowStatuses && Object.keys(props.groupedWorkflowStatuses).length > 0) {
    // Flatten grouped statuses
    const options = []
    for (const [key, group] of Object.entries(props.groupedWorkflowStatuses)) {
      const groupLabel = group.project
        ? `${group.project.title} - ${group.workflow.name} (${group.taskCategory.name})`
        : `${group.workflow.name} (${group.taskCategory.name})`
      for (const status of group.statuses) {
        options.push({
          id: status.id.toString(),
          title: `${groupLabel} - ${status.name}`,
          color: status.color || '#6B7280',
        })
      }
    }
    return options
  } else {
    return (props.workflowStatuses || []).map(s => ({
      id: s.id.toString(),
      title: s.name,
      color: s.color || '#6B7280',
    }))
  }
})

// Compute member options for MultiSelect (grouped by project if available)
const memberOptions = computed(() => {
  if (props.groupedMembers && Object.keys(props.groupedMembers).length > 0) {
    // Flatten grouped members - allow same member to appear in multiple projects
    const options = []
    for (const [projectTitle, projectMembers] of Object.entries(props.groupedMembers)) {
      for (const member of projectMembers) {
        options.push({
          id: member.id.toString(),
          title: `${projectTitle} - ${member.username || member.name}`,
          color: '#6B7280',
        })
      }
    }
    return options
  } else {
    return props.members.map(m => ({
      id: m.id.toString(),
      title: m.username || m.name,
      color: '#6B7280',
    }))
  }
})

// Compute label options for MultiSelect (grouped by project if available)
const labelOptions = computed(() => {
  if (props.groupedLabels && Object.keys(props.groupedLabels).length > 0) {
    // Flatten grouped labels
    const options = []
    for (const [projectTitle, projectLabels] of Object.entries(props.groupedLabels)) {
      for (const label of projectLabels) {
        options.push({
          id: label.id,
          title: `${projectTitle} - ${label.title}`,
          color: label.color,
        })
      }
    }
    return options
  } else {
    return props.labels
  }
})

const isExpanded = ref(props.initiallyExpanded)
const personalLabels = ref([])
const localFilters = ref({
  search: '',
  authorIds: [],
  assigneeIds: [],
  priorities: [],
  workflowStatusIds: [],
  taskCategoryIds: [],
  labelIds: [],
  personalLabelIds: [],
  projectIds: [],
  dueFrom: '',
  dueTo: '',
  dueInDays: null,
  includeFinished: false,
  includeArchived: false,
  includeOverdue: true, // Default to true
})

// GraphQL query for personal labels
const PERSONAL_LABELS_QUERY = gql`
  query PersonalLabels {
    personalLabels {
      id
      title
      color
    }
  }
`

// Lazy query for personal labels
const { load: loadPersonalLabels, result: personalLabelsResult } = useLazyQuery(
  PERSONAL_LABELS_QUERY,
  () => ({}),
  () => ({
    enabled: props.personalLabelsEnabled,
  })
)

// Watch for personal labels enabled to load personal labels
watch(
  () => props.personalLabelsEnabled,
  enabled => {
    if (enabled && !personalLabelsResult.value) {
      loadPersonalLabels()
    }
  },
  { immediate: true }
)

// Watch for personal labels query result
watch(
  personalLabelsResult,
  data => {
    if (data?.personalLabels) {
      personalLabels.value = data.personalLabels || []
    }
  },
  { immediate: true }
)

// Track if filters have been initialized to prevent initial auto-emit
const filtersInitialized = ref(false)
// Track when we're updating from props to prevent auto-emit loop
const updatingFromProps = ref(false)

// Initialize from props
watch(
  () => props.initialFilters,
  newFilters => {
    updatingFromProps.value = true
    // Create new array references to ensure reactivity
    localFilters.value = {
      search: newFilters.search || '',
      authorIds: newFilters.authorIds ? [...newFilters.authorIds] : [],
      assigneeIds: newFilters.assigneeIds ? [...newFilters.assigneeIds] : [],
      priorities: newFilters.priorities ? [...newFilters.priorities] : [],
      workflowStatusIds: newFilters.workflowStatusIds ? [...newFilters.workflowStatusIds] : [],
      taskCategoryIds: newFilters.taskCategoryIds ? [...newFilters.taskCategoryIds] : [],
      labelIds: newFilters.labelIds ? [...newFilters.labelIds] : [],
      personalLabelIds: newFilters.personalLabelIds ? [...newFilters.personalLabelIds] : [],
      projectIds: newFilters.projectIds ? [...newFilters.projectIds] : [],
      dueFrom: newFilters.dueFrom || '',
      dueTo: newFilters.dueTo || '',
      dueInDays: newFilters.dueInDays || null,
      includeFinished: newFilters.includeFinished || false,
      includeArchived: newFilters.includeArchived || false,
      includeOverdue: newFilters.includeOverdue !== undefined ? newFilters.includeOverdue : true,
    }
    // Mark as initialized after first load
    filtersInitialized.value = true
    // Reset flag after a tick to allow reactivity to settle
    nextTick(() => {
      updatingFromProps.value = false
    })
  },
  { immediate: true, deep: true }
)

// Auto-emit changes when hideApplyButton is true (but not on initial load or when updating from props)
watch(
  localFilters,
  () => {
    if (props.hideApplyButton && filtersInitialized.value && !updatingFromProps.value) {
      // Validate search before emitting
      const search = localFilters.value.search
      if (search && search.trim().length > 0 && search.trim().length < 3) {
        return // Don't emit if search is invalid
      }
      // Validate dueInDays before emitting
      if (localFilters.value.dueInDays !== null && dueInDaysError.value) {
        return // Don't emit if dueInDays is invalid
      }
      emit('apply', { ...localFilters.value })
    }
  },
  { deep: true }
)

const searchError = computed(() => {
  const search = localFilters.value.search
  return search && search.trim().length > 0 && search.trim().length < 3
})

const dueInDaysError = computed(() => {
  const days = localFilters.value.dueInDays
  if (days === null || days === undefined || days === '') {
    return null // Empty is valid (filter not applied)
  }
  const numDays = Number(days)
  if (isNaN(numDays) || !Number.isInteger(numDays)) {
    return s__('TaskFilter|Must be a number between 1 and 99')
  }
  if (numDays < 1 || numDays > 99) {
    return s__('TaskFilter|Must be between 1 and 99')
  }
  return null
})

const activeFiltersCount = computed(() => {
  let count = 0
  const search = localFilters.value.search
  if (search && search.trim().length >= 3) count++
  if (!props.isPersonalProject) {
    if (
      !props.hideAuthors &&
      localFilters.value.authorIds &&
      localFilters.value.authorIds.length > 0
    )
      count++
    if (
      !props.hideAssignees &&
      localFilters.value.assigneeIds &&
      localFilters.value.assigneeIds.length > 0
    )
      count++
  }
  if (localFilters.value.priorities && localFilters.value.priorities.length > 0) count++
  if (localFilters.value.workflowStatusIds && localFilters.value.workflowStatusIds.length > 0)
    count++
  if (
    !props.hideProjectLabels &&
    localFilters.value.labelIds &&
    localFilters.value.labelIds.length > 0
  )
    count++
  if (localFilters.value.personalLabelIds && localFilters.value.personalLabelIds.length > 0) count++
  if (localFilters.value.projectIds && localFilters.value.projectIds.length > 0) count++
  if (localFilters.value.dueFrom && localFilters.value.dueFrom.trim().length > 0) count++
  if (localFilters.value.dueTo && localFilters.value.dueTo.trim().length > 0) count++
  if (
    localFilters.value.dueInDays !== null &&
    localFilters.value.dueInDays !== undefined &&
    localFilters.value.dueInDays !== ''
  )
    count++
  if (!props.hideIncludeFinished && localFilters.value.includeFinished) count++
  if (localFilters.value.includeArchived) count++
  if (localFilters.value.includeOverdue === false) count++ // Count as active if explicitly set to false (non-default)
  if (localFilters.value.taskCategoryIds && localFilters.value.taskCategoryIds.length > 0) count++
  return count
})

const filterContentRef = ref(null)

function toggleExpanded(event) {
  if (props.lockExpanded) {
    return
  }

  // Don't toggle if click is inside filter-content
  const target = event?.target
  if (target) {
    // Check if target is inside a .filter-content element
    const filterContent = target.closest?.('.filter-content')
    if (filterContent && filterContent !== event?.currentTarget) {
      return
    }

    // Only allow toggle if click is on the filter-header itself
    const filterHeader = target.closest?.('.filter-header')
    if (filterHeader !== event?.currentTarget) {
      return
    }
  }

  isExpanded.value = !isExpanded.value
}

function applyFilters() {
  // Don't apply if search query is too short
  const search = localFilters.value.search
  if (search && search.trim().length > 0 && search.trim().length < 3) {
    return
  }
  // Don't apply if dueInDays is invalid
  if (dueInDaysError.value) {
    return
  }
  emit('apply', { ...localFilters.value })
}

function handleAssigneeIdsUpdate(ids) {
  localFilters.value.assigneeIds = [...ids]
}

function getCurrentFilters() {
  return { ...localFilters.value }
}

function resetFilters() {
  updatingFromProps.value = true
  localFilters.value = {
    search: '',
    authorIds: [],
    assigneeIds: [],
    priorities: [],
    workflowStatusIds: [],
    taskCategoryIds: [],
    labelIds: [],
    personalLabelIds: [],
    projectIds: [],
    dueFrom: '',
    dueTo: '',
    dueInDays: null,
    includeFinished: false,
    includeArchived: false,
    includeOverdue: true, // Default to true
  }
  nextTick(() => {
    updatingFromProps.value = false
    if (!props.hideApplyButton) {
      emit('apply', { ...localFilters.value })
    }
  })
}

// Expose methods for parent components
defineExpose({
  getCurrentFilters,
  applyFilters,
  resetFilters,
})
</script>

<style src="../../styles/forms.css"></style>
<style scoped>
.filter-panel {
  margin-bottom: 16px;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.filter-header:hover {
  background-color: #e9ecef;
}

.expand-icon {
  font-size: 12px;
  color: #666;
}

.active-count {
  color: #1d6bf3;
  font-weight: 700;
}

.filter-content {
  padding: 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 8px 8px;
}

.filter-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.filter-col {
  flex: 1;
  min-width: 0;
}

.filter-col:has(.checkbox-label:only-child) {
  display: flex;
  align-items: center;
}

.filter-col-full {
  flex: 1;
}

.filter-col label,
.filter-col-full label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
}

.filter-col-full select,
.filter-col select,
.filter-col input[type='date'],
.filter-col-full input[type='date'] {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: normal;
}

.checkbox-label input[type='checkbox'] {
  cursor: pointer;
  width: auto;
  margin: 0;
}

.label-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.label-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.label-checkbox input[type='checkbox'] {
  cursor: pointer;
}

.label-badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
}

.filter-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.btn-primary {
  flex: 1;
}

.search-input-invalid {
  border-color: #dc3545 !important;
}

.search-error {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.input-invalid {
  border-color: #dc3545 !important;
}

.input-error {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

@media (max-width: 640px) {
  .filter-row {
    flex-direction: column;
  }
}

@media (max-width: 767px) {
  .filter-col-full select,
  .filter-col select,
  .filter-col input[type='date'],
  .filter-col-full input[type='date'],
  .filter-col-full input[type='text'],
  .filter-col input[type='number'] {
    font-size: 16px;
  }
}
</style>
